import { useEffect, useState } from "react";
import moment from "moment";
import customAxios from "../../../../utils/customAxios";
import {
    normalizeTerminalValue,
    getLocationFromFormattedAddress,
    mapPaxToState,
    mapLuggageToState,
    mapVehiclesToState,
    extractMessage,
    parseTitleAndName,
    getLongDistanceFeeFormValues,
} from "../utils";

export function useBookingDetail({
    id,
    requestId,
    lang,
    setTransferType,
    setTransferFlags,
    setPickupLocation,
    setPickupLocationData,
    setDropoffLocation,
    setDropoffLocationData,
    setAirportDropoffLocations,
    setTravelDate,
    setFormatedTravelDate,
    setPickupTime,
    setDuration,
    setPassengers,
    setLuggage,
    setVehicles,
    setChildSeatAllocations,
    setBookingFor,
    setPassengerName,
    setPassengerTitle,
    setCustomer,
    setBookingKey,
    setBookingStatus,
    setPaymentStatus,
    setPrefixPhoneNumber,
    setPhoneNumber,
    setCountryCode,
    setMeetAssistS,
    setPickupFlightNo,
    setDropoffFlightNo,
    setPickupTerminal,
    setDropoffTerminal,
    setPickupSignHonorific,
    setPickupSign,
    setMessage,
    setTravelArea,
    setBasePriceType,
    setGarageType,
    setDiscountType,
    setDiscountValue,
    setLongDistanceFeeType,
    setLongDistanceFeeValue,
    setDepartureGarageId,
    setReturnGarageId,
    setGarageOptions,
}) {
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchBookingDetail = async () => {
            if (!id) return;

            setIsLoading(true);
            try {
                const queryString = new URLSearchParams({
                    id,
                    isAbandoned: "false",
                    limit: "10",
                    page: "1",
                }).toString();

                const response = await customAxios.get(`/api/admin/bookings?${queryString}`);
                const bookingDetail = response?.data?.data?.data?.[0];

                if (!bookingDetail) return;

                const transferTypeValue = bookingDetail?.bookingType === "byTheHour" ? "charter" : "airport";
                setTransferType(transferTypeValue);
                setTransferFlags({
                    isAirportTransfer: Boolean(bookingDetail?.isAirportTransfer),
                    isFromAirport: Boolean(bookingDetail?.isFromAirport),
                    isToAirport: Boolean(bookingDetail?.isToAirport),
                    isStationTransfer: Boolean(bookingDetail?.isStationTransfer),
                    isFromStation: Boolean(bookingDetail?.isFromStation),
                    isToStation: Boolean(bookingDetail?.isToStation),
                });

                const pickupAddress = getLocationFromFormattedAddress(
                    bookingDetail?.pickupLocation?.formatted_address
                );
                const destinationAddresses = (bookingDetail?.destinations || [])
                    .map((destination) =>
                        getLocationFromFormattedAddress(destination?.formatted_address)
                    )
                    .filter(Boolean);

                setPickupLocation(pickupAddress || "");
                setPickupLocationData(bookingDetail?.pickupLocation || null);

                if (destinationAddresses.length > 0) {
                    setDropoffLocation(destinationAddresses[0]);
                    setDropoffLocationData(bookingDetail?.destinations?.[0] || null);
                    setAirportDropoffLocations(
                        (bookingDetail?.destinations || []).map((destination, index) => ({
                            id: index + 1,
                            address: destination?.formatted_address || "",
                            data: destination || null,
                        }))
                    );
                } else {
                    setDropoffLocation("");
                    setDropoffLocationData(null);
                    setAirportDropoffLocations([{ id: 1, address: "", data: null }]);
                }

                const pickupDateObj = bookingDetail?.pickupTime
                    ? new Date(bookingDetail.pickupDateTime)
                    : null;
                if (pickupDateObj && !Number.isNaN(pickupDateObj.getTime())) {
                    console.log("Parsed pickup date:", pickupDateObj);
                    setTravelDate(pickupDateObj);
                    setFormatedTravelDate(moment(pickupDateObj).format("YYYY-MM-DD"));
                    setPickupTime(moment(pickupDateObj).format("HH:mm"));
                }

                if (bookingDetail?.duration) {
                    const durationString = String(bookingDetail.duration);
                    const [hoursRaw = "0", minutesRaw = "0"] = durationString.split(":");
                    const hours = String(Number(hoursRaw) || 0).padStart(2, "0");
                    const minutes = String(Number(minutesRaw) || 0).padStart(2, "0");
                    setDuration({
                        value: `${hours}:${minutes}`,
                        label:
                            lang === "EN" || lang === "en"
                                ? `${hours} Hours ${minutes} Minutes`
                                : `${hours}時間 ${minutes}分`,
                    });
                }

                const mappedVehicles = mapVehiclesToState(bookingDetail?.bookingInfo);
                setPassengers(mapPaxToState(bookingDetail?.bookingInfo));
                setLuggage(mapLuggageToState(bookingDetail?.bookingInfo));
                setVehicles(mappedVehicles);

                setChildSeatAllocations(
                    mappedVehicles.map((vehicle, index) => {
                        const bookingInfoItem = bookingDetail?.bookingInfo?.[index] || {};
                        return {
                            infant: Number(bookingInfoItem?.childSeat[0]?.childSeatCount || 0),
                            toddler: Number(bookingInfoItem?.childSeat[1]?.childSeatCount || 0),
                            child: Number(bookingInfoItem?.childSeat[2]?.childSeatCount || 0),
                        };
                    })
                );

                const passengerFullName = bookingDetail?.passengerName || "";
                const customerFullName =
                    `${bookingDetail?.customer?.pronounced || ""} ${bookingDetail?.customer?.name || ""}`.trim();
                setBookingFor(
                    passengerFullName &&
                        customerFullName &&
                        passengerFullName.trim() !== customerFullName
                        ? "someone_else"
                        : "myself"
                );
                setPassengerName(passengerFullName || "");

                setCustomer(bookingDetail?.customer || {});
                setBookingKey(bookingDetail?.key || "");
                const rawStatus = String(
                    bookingDetail?.bookingStatus ||
                    ""
                ).toUpperCase();
                const rawPaymentStatus = String(
                    bookingDetail?.paymentStatus ||
                    ""
                ).toUpperCase();
                setBookingStatus(rawStatus);
                setPaymentStatus(rawPaymentStatus)

                const passengerPrefix = bookingDetail?.passengerPrefixNumber || "+81";
                setPrefixPhoneNumber(passengerPrefix);
                setPhoneNumber(bookingDetail?.passengerPhoneNumber || "");
                setCountryCode(bookingDetail?.customer?.country_code || "JP");

                const normalizeMeetAssistName = (name) => {
                    const normalized = String(name || "").toLowerCase();
                    if (normalized.includes("vip")) return "vipMeetAssist";
                    if (normalized.includes("meet")) return "meetAssist";
                    return "none";
                };

                const arrivalMeetAssist = bookingDetail?.meetAssist?.find(
                    (item) => item?.meetAssistType === "arrival"
                );
                const departureMeetAssist = bookingDetail?.meetAssist?.find(
                    (item) => item?.meetAssistType === "departure"
                );

                setMeetAssistS([
                    {
                        meetAssistType: "arrival",
                        meetAssistName: normalizeMeetAssistName(arrivalMeetAssist?.meetAssistName),
                        meetAssistPrice: Number(arrivalMeetAssist?.meetAssistPrice || 0),
                    },
                    {
                        meetAssistType: "departure",
                        meetAssistName: normalizeMeetAssistName(departureMeetAssist?.meetAssistName),
                        meetAssistPrice: Number(departureMeetAssist?.meetAssistPrice || 0),
                    },
                ]);

                setPickupFlightNo(bookingDetail?.pickupFlightNo || "");
                setDropoffFlightNo(bookingDetail?.dropoffFlightNo || "");
                setPickupTerminal(normalizeTerminalValue(bookingDetail?.pickupTerminal || ""));
                setDropoffTerminal(normalizeTerminalValue(bookingDetail?.dropoffTerminal || ""));

                const pickupSign = parseTitleAndName(bookingDetail?.pickupSign || "");
                setPickupSignHonorific(pickupSign.title || "Mr.");
                setPickupSign(pickupSign.name || "");

                setMessage(extractMessage(bookingDetail?.message));

                if (bookingDetail?.travelArea) {
                    setTravelArea(bookingDetail.travelArea);
                }

                const TOKYO_GARAGE_ID = "673821569a84bf24cd191782";
                const bookingGarageId = bookingDetail?.garage?._id || bookingDetail?.garage || "";
                const bookingReturnGarageId = bookingDetail?.returnGarage?._id || bookingDetail?.returnGarage || "";
                const departureGarage = String(bookingGarageId || "");
                const arrivalGarage = String(bookingReturnGarageId || "");

                setDepartureGarageId(departureGarage);
                setReturnGarageId(arrivalGarage);
                // "custom" whenever the booking isn't on the standard Tokyo garage for
                // both dispatch and arrival — so an admin can see/adjust a manually-set
                // garage even when both garages are the same non-Tokyo garage.
                const isDefaultTokyoGarage =
                    (!departureGarage || departureGarage === TOKYO_GARAGE_ID) &&
                    (!arrivalGarage || arrivalGarage === TOKYO_GARAGE_ID);
                setGarageType(isDefaultTokyoGarage ? "default" : "custom");

                const isOutsideTokyoBasePrice =
                    departureGarage &&
                    arrivalGarage &&
                    departureGarage !== TOKYO_GARAGE_ID &&
                    arrivalGarage !== TOKYO_GARAGE_ID;
                setBasePriceType(isOutsideTokyoBasePrice ? "outsideTokyo" : "tlfPrice");

                const hasDiscount =
                    Boolean(bookingDetail?.discount?.fromQuotation) || Number(bookingDetail?.discount?.rate || 0) > 0;
                setDiscountType(hasDiscount ? "applicable" : "notApplicable");
                setDiscountValue(Number(bookingDetail?.discount?.rate || 0));

                const bookingLongDistanceFee = getLongDistanceFeeFormValues(bookingDetail);
                setLongDistanceFeeType(bookingLongDistanceFee.feeType);
                setLongDistanceFeeValue(bookingLongDistanceFee.feeValue);

                try {
                    const garagesResponse = await customAxios.get("/api/admin/garages");
                    const garageOptions = (garagesResponse?.data || [])
                        .map((garage) => ({
                            value: String(garage?._id || ""),
                            label: garage?.name || garage?.prefecture || String(garage?._id || ""),
                        }))
                        .filter((option) => option.value);
                    setGarageOptions(garageOptions);
                } catch (garageError) {
                    console.error("Error loading garage data:", garageError);
                    setGarageOptions([]);
                }

                // If editing a draft modification, override form values with the draft's editBooking data
                if (requestId) {
                    try {
                        const modReqResponse = await customAxios.get(`/api/admin/request-change-booking?id=${requestId}&limit=1`);
                        const eb = modReqResponse?.data?.data?.data?.[0]?.editBooking;
                        if (eb) {
                            const ebTransferType = eb?.bookingType === "byTheHour" ? "charter" : "airport";
                            setTransferType(ebTransferType);
                            setTransferFlags({
                                isAirportTransfer: Boolean(eb?.isAirportTransfer),
                                isFromAirport: Boolean(eb?.isFromAirport),
                                isToAirport: Boolean(eb?.isToAirport),
                                isStationTransfer: Boolean(eb?.isStationTransfer),
                                isFromStation: Boolean(eb?.isFromStation),
                                isToStation: Boolean(eb?.isToStation),
                            });

                            if (eb?.pickupLocation) {
                                setPickupLocation(getLocationFromFormattedAddress(eb.pickupLocation?.formatted_address) || "");
                                setPickupLocationData(eb.pickupLocation);
                            }

                            const ebDestinations = eb?.destinations || [];
                            if (ebDestinations.length > 0) {
                                setDropoffLocation(getLocationFromFormattedAddress(ebDestinations[0]?.formatted_address) || "");
                                setDropoffLocationData(ebDestinations[0] || null);
                                setAirportDropoffLocations(
                                    ebDestinations.map((destination, index) => ({
                                        id: index + 1,
                                        address: destination?.formatted_address || "",
                                        data: destination || null,
                                    }))
                                );
                            }

                            const ebPickupDateObj = eb?.pickupDateTime ? new Date(eb.pickupDateTime) : null;
                            if (ebPickupDateObj && !Number.isNaN(ebPickupDateObj.getTime())) {
                                setTravelDate(ebPickupDateObj);
                                setFormatedTravelDate(moment(ebPickupDateObj).format("YYYY-MM-DD"));
                                setPickupTime(moment(ebPickupDateObj).format("HH:mm"));
                            }

                            if (eb?.duration) {
                                const durationString = String(eb.duration);
                                const [hoursRaw = "0", minutesRaw = "0"] = durationString.split(":");
                                const hours = String(Number(hoursRaw) || 0).padStart(2, "0");
                                const minutes = String(Number(minutesRaw) || 0).padStart(2, "0");
                                setDuration({
                                    value: `${hours}:${minutes}`,
                                    label: lang === "EN" || lang === "en"
                                        ? `${hours} Hours ${minutes} Minutes`
                                        : `${hours}時間 ${minutes}分`,
                                });
                            }

                            if (eb?.bookingInfo) {
                                const ebMappedVehicles = mapVehiclesToState(eb.bookingInfo);
                                setPassengers(mapPaxToState(eb.bookingInfo));
                                setLuggage(mapLuggageToState(eb.bookingInfo));
                                setVehicles(ebMappedVehicles);
                                setChildSeatAllocations(
                                    ebMappedVehicles.map((vehicle, index) => {
                                        const infoItem = eb.bookingInfo[index] || {};
                                        return {
                                            infant: Number(infoItem?.childSeat?.[0]?.childSeatCount || 0),
                                            toddler: Number(infoItem?.childSeat?.[1]?.childSeatCount || 0),
                                            child: Number(infoItem?.childSeat?.[2]?.childSeatCount || 0),
                                        };
                                    })
                                );
                            }

                            if (eb?.passengerName !== undefined) setPassengerName(eb.passengerName || "");
                            if (eb?.passengerPronounced && setPassengerTitle) setPassengerTitle(eb.passengerPronounced);
                            if (eb?.passengerPrefixNumber) setPrefixPhoneNumber(eb.passengerPrefixNumber);
                            if (eb?.passengerPhoneNumber !== undefined) setPhoneNumber(eb.passengerPhoneNumber || "");
                            if (eb?.pickupFlightNo !== undefined) setPickupFlightNo(eb.pickupFlightNo || "");
                            if (eb?.dropoffFlightNo !== undefined) setDropoffFlightNo(eb.dropoffFlightNo || "");
                            if (eb?.pickupTerminal !== undefined) setPickupTerminal(normalizeTerminalValue(eb.pickupTerminal || ""));
                            if (eb?.dropoffTerminal !== undefined) setDropoffTerminal(normalizeTerminalValue(eb.dropoffTerminal || ""));
                            if (eb?.travelArea) setTravelArea(eb.travelArea);
                            if (eb?.message !== undefined) setMessage(extractMessage(eb.message));

                            if (eb?.meetAssist) {
                                const normalizeMeetAssistName = (name) => {
                                    const normalized = String(name || "").toLowerCase();
                                    if (normalized.includes("vip")) return "vipMeetAssist";
                                    if (normalized.includes("meet")) return "meetAssist";
                                    return "none";
                                };
                                const arrivalMA = eb.meetAssist.find(item => item?.meetAssistType === "arrival");
                                const departureMA = eb.meetAssist.find(item => item?.meetAssistType === "departure");
                                setMeetAssistS([
                                    { meetAssistType: "arrival", meetAssistName: normalizeMeetAssistName(arrivalMA?.meetAssistName), meetAssistPrice: Number(arrivalMA?.meetAssistPrice || 0) },
                                    { meetAssistType: "departure", meetAssistName: normalizeMeetAssistName(departureMA?.meetAssistName), meetAssistPrice: Number(departureMA?.meetAssistPrice || 0) },
                                ]);
                            }

                            if (eb?.pickupSign !== undefined || eb?.pickupSignHonorific !== undefined) {
                                const ebPickupSign = parseTitleAndName(eb?.pickupSign || "");
                                setPickupSignHonorific(eb?.pickupSignHonorific || ebPickupSign.title || "Mr.");
                                setPickupSign(ebPickupSign.name || eb?.pickupSign || "");
                            }

                            // Restore the settings section (garage + discount) from the
                            // saved draft so reopening it shows what was submitted, not the
                            // original booking's values.
                            const ebDepartureGarage = String(eb?.garage?._id || eb?.garage || "");
                            const ebArrivalGarage = String(eb?.returnGarage?._id || eb?.returnGarage || "");
                            if (ebDepartureGarage || ebArrivalGarage) {
                                setDepartureGarageId(ebDepartureGarage);
                                setReturnGarageId(ebArrivalGarage);
                                const ebIsDefaultTokyoGarage =
                                    (!ebDepartureGarage || ebDepartureGarage === TOKYO_GARAGE_ID) &&
                                    (!ebArrivalGarage || ebArrivalGarage === TOKYO_GARAGE_ID);
                                setGarageType(ebIsDefaultTokyoGarage ? "default" : "custom");
                            }

                            const ebDiscount = Number(eb?.discount?.rate || 0);
                            if (eb?.discount !== undefined) {
                                setDiscountType(
                                    eb?.discount?.fromQuotation || ebDiscount > 0 ? "applicable" : "notApplicable"
                                );
                                setDiscountValue(ebDiscount);
                            }

                            // Restore the long distance fee from the draft too.
                            if (eb?.bookingInfo?.length > 0) {
                                const draftLongDistanceFee = getLongDistanceFeeFormValues(eb);
                                setLongDistanceFeeType(draftLongDistanceFee.feeType);
                                setLongDistanceFeeValue(draftLongDistanceFee.feeValue);
                            }
                        }
                    } catch (modReqError) {
                        console.error("Error loading modification request detail:", modReqError);
                    }
                }
            } catch (error) {
                console.error("Error loading booking detail:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchBookingDetail();
    }, [id, requestId]);

    return { isLoading };
}
